import { useState, useRef, useMemo } from "react";
import { useInvestigations, useInvestigationTemplates, useInvestigationCategories, useInvestigationTemplateGroups, type Investigation, type InvestigationTemplate, type InvestigationTemplateGroup } from "@/lib/queries";
import {
  useCreateInvestigation, useUpdateInvestigation, useDeleteInvestigation, useBulkDeleteInvestigations,
  useCreateInvestigationTemplate, useUpdateInvestigationTemplate, useDeleteInvestigationTemplate,
  useCreateInvestigationCategory, useDeleteInvestigationCategory,
  useCreateInvestigationTemplateGroup, useUpdateInvestigationTemplateGroup, useDeleteInvestigationTemplateGroup,
  useMoveInvestigationTemplate,
  useBulkImportInvestigations,
} from "@/lib/mutations";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Pencil, Trash, Upload, Download, Search, FolderPlus, FolderOpen, FolderInput, X } from "lucide-react";
import { bucketTemplates, UNGROUPED_LABEL } from "@/lib/templateGroups";
import { apiFetchBlob } from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useDropdownNav } from "@/hooks/useDropdownNav";

const UNCATEGORIZED = "Uncategorized";

/** Group investigations by category; tests are items inside their category. */
function groupByCategory(rows: Investigation[]) {
  const map = new Map<string, Investigation[]>();
  for (const r of rows) {
    const key = r.category?.trim() || UNCATEGORIZED;
    const list = map.get(key) ?? [];
    list.push(r);
    map.set(key, list);
  }
  return [...map.entries()]
    .sort((a, b) => (a[0] === UNCATEGORIZED ? 1 : b[0] === UNCATEGORIZED ? -1 : a[0].localeCompare(b[0])))
    .map(([category, items]) => ({ category, items }));
}

export default function InvestigationsPage() {
  return (
    <div className="mx-auto max-w-7xl p-6 space-y-4">
      <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">Investigations Catalog</h1>
      <Tabs defaultValue="items">
        <TabsList>
          <TabsTrigger value="items">Items</TabsTrigger>
          <TabsTrigger value="categories">Categories</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="groups">Groups</TabsTrigger>
          <TabsTrigger value="bulk">Bulk import</TabsTrigger>
        </TabsList>
        <TabsContent value="items"><ItemsTab /></TabsContent>
        <TabsContent value="categories"><CategoriesTab /></TabsContent>
        <TabsContent value="templates"><TemplatesTab /></TabsContent>
        <TabsContent value="groups"><GroupsTab /></TabsContent>
        <TabsContent value="bulk"><BulkImportTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function ItemsTab() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("investigations.create");
  const canEdit = isAdmin || permissions.has("investigations.edit");
  const canDelete = isAdmin || permissions.has("investigations.delete");
  const { data, isLoading } = useInvestigations();
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<Investigation | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [search, setSearch] = useState("");
  // Session counter bumps on every open so the dialog remounts fresh each time
  // and useState re-initializes from the current item prop (see SCHEMA.md §18 2026-07-20).
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditItem(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (d: Investigation) => { setEditItem(d); setSessionId((n) => n + 1); setAddOpen(true); };
  const create = useCreateInvestigation();
  const update = useUpdateInvestigation();
  const del = useDeleteInvestigation();
  const bulkDel = useBulkDeleteInvestigations();
  const { data: catData } = useInvestigationCategories();
  const allRows = data?.rows ?? [];
  // Flat alphabetical listing with client-side search (name + category).
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter((r) => r.name.toLowerCase().includes(q) || (r.category ?? "").toLowerCase().includes(q));
  }, [allRows, search]);
  const categoryOptions = catData?.rows ?? [];

  const allSelected = rows.length > 0 && selected.size === rows.length;
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)));
  const toggleOne = (id: string) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const doBulkDelete = async () => {
    try {
      await bulkDel.mutateAsync([...selected]);
      setSelected(new Set());
      setConfirmOpen(false);
    } catch { /* handled */ }
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center justify-between gap-2 p-4">
          <h2 className="font-serif text-lg text-maroon-700 dark:text-amber-500">
            Items ({allRows.length})
          </h2>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
              <Input
                className="w-56 pl-8"
                placeholder="Search name or category…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {canDelete && selected.size > 0 && (
              <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
                <Trash size={14} className="mr-1" /> Delete selected ({selected.size})
              </Button>
            )}
            {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}><Plus size={14} className="mr-1" /> Add</Button>}
          </div>
        </div>
        <Table>
          <TableHeader>
            <TableRow className="bg-cream-100 text-maroon-700 dark:bg-dark-700 dark:text-amber-500">
              {canDelete && (
                <TableHead className="w-10">
                  <Checkbox
                    checked={allSelected ? true : selected.size > 0 ? "indeterminate" : false}
                    onCheckedChange={toggleAll}
                    aria-label="Select all investigations"
                  />
                </TableHead>
              )}
              <TableHead>Name</TableHead><TableHead>Category</TableHead><TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? <TableRow><TableCell colSpan={canDelete ? 4 : 3}><Skeleton className="h-10" /></TableCell></TableRow> :
             rows.length === 0 ? <TableRow><TableCell colSpan={canDelete ? 4 : 3} className="text-center text-sm text-muted py-8">{search ? "No investigations match your search" : "No investigations"}</TableCell></TableRow> :
             rows.map((d: Investigation) => (
              <TableRow key={d.id} className="even:bg-cream-50/50">
                {canDelete && (
                  <TableCell>
                    <Checkbox
                      checked={selected.has(d.id)}
                      onCheckedChange={() => toggleOne(d.id)}
                      aria-label={`Select ${d.name}`}
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium">{d.name}</TableCell>
                <TableCell className="text-xs text-muted">{d.category ?? "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(d)}><Pencil size={14} /></Button>}
                    {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => del.mutate(d.id)}><Trash size={14} /></Button>}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {search && rows.length < allRows.length && (
          <p className="px-4 py-2 text-xs text-muted">Showing {rows.length} of {allRows.length} items</p>
        )}
      </CardContent>
      <ItemFormDialog key={sessionId} open={addOpen} onOpenChange={setAddOpen} item={editItem} categoryOptions={categoryOptions} create={create} update={update} />
      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Delete selected investigations?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted">
            {selected.size} item{selected.size === 1 ? "" : "s"} will be permanently deleted and removed from any
            templates that reference them. This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={doBulkDelete} disabled={bulkDel.isPending}>
              {bulkDel.isPending ? "Deleting…" : "Delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// ---------------- Categories tab (accordion view of category → tests) ----------------

function CategoriesTab() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("investigations.create");
  const canEdit = isAdmin || permissions.has("investigations.edit");
  const canDelete = isAdmin || permissions.has("investigations.delete");
  const { data: invData, isLoading: invLoading } = useInvestigations();
  const { data: catData, isLoading: catLoading } = useInvestigationCategories();
  const investigations = invData?.rows ?? [];
  const groups = useMemo(() => groupByCategory(investigations), [investigations]);
  // rows = union of registered categories (may be empty of tests) and the
  // catalog's distinct values; all 7 are now registered (2026-09-19 backfill).
  // Show all union categories; registered-but-empty ones get 0 tests.
  const known = catData?.rows ?? [];
  const sections = useMemo(() => {
    const byName = new Map(groups.map((g) => [g.category, g.items]));
    const names = new Set<string>([...known, ...groups.map((g) => g.category)]);
    return [...names]
      .sort((a, b) => (a === UNCATEGORIZED ? 1 : b === UNCATEGORIZED ? -1 : a.localeCompare(b)))
      .map((category) => ({ category, items: byName.get(category) ?? [] }));
  }, [groups, known]);

  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<Investigation | null>(null);
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditItem(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (d: Investigation) => { setEditItem(d); setSessionId((n) => n + 1); setAddOpen(true); };
  const createInv = useCreateInvestigation();
  const updateInv = useUpdateInvestigation();
  const delInv = useDeleteInvestigation();
  const createCat = useCreateInvestigationCategory();

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg text-maroon-700 dark:text-amber-500">Categories ({sections.length})</h2>
          {canCreate && (
            <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}>
              <FolderPlus size={14} className="mr-1" /> Add category
            </Button>
          )}
        </div>
        {catLoading && invLoading ? <Skeleton className="h-10" /> :
         sections.length === 0 ? <p className="text-sm text-muted py-8 text-center">No categories yet — add one or import tests with categories.</p> :
         <Accordion type="multiple" className="w-full">
          {sections.map(({ category, items }) => (
            <AccordionItem key={category} value={category}>
              <AccordionTrigger className="text-maroon-700 dark:text-amber-500 hover:no-underline">
                <span className="flex items-center gap-2">
                  <span className="font-serif">{category}</span>
                  <Badge variant="secondary">{items.length} tests</Badge>
                </span>
              </AccordionTrigger>
              <AccordionContent>
                {items.length === 0 ? (
                  <p className="text-sm text-muted py-2">No tests in this category yet — add tests and assign them here.</p>
                ) : (
                  <ul className="space-y-1">
                    {items.map((inv) => (
                      <li key={inv.id} className="flex items-center justify-between rounded px-2 py-1 hover:bg-cream-50 dark:hover:bg-dark-800/60">
                        <span className="text-sm">{inv.name}</span>
                        <span className="flex gap-1">
                          {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(inv)}><Pencil size={14} /></Button>}
                          {canDelete && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500" onClick={() => delInv.mutate(inv.id)}><Trash size={14} /></Button>}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
         </Accordion>}
      </CardContent>
      <CategoryFormDialog open={addOpen} onOpenChange={setAddOpen} existing={known} create={createCat} />
      <ItemFormDialog key={sessionId} open={editItem !== null} onOpenChange={(v) => { if (!v) setEditItem(null); }} item={editItem} categoryOptions={known} create={createInv} update={updateInv} />
    </Card>
  );
}

function CategoryFormDialog({ open, onOpenChange, existing, create }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existing: string[];
  create: ReturnType<typeof useCreateInvestigationCategory>;
}) {
  const [name, setName] = useState("");
  const trimmed = name.trim();
  const dupe = trimmed.length > 0 && existing.some((c) => c.toLowerCase() === trimmed.toLowerCase());
  const submit = async () => {
    if (!trimmed || dupe) return;
    try {
      await create.mutateAsync(trimmed);
      setName("");
      onOpenChange(false);
    } catch { /* handled */ }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setName(""); onOpenChange(v); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Add category</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input
              autoFocus
              value={name}
              placeholder="e.g. HEMATOLOGY"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            />
          </div>
          {dupe && <p className="text-sm text-red-500">A category with this name already exists.</p>}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!trimmed || dupe || create.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Add</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ItemFormDialog({ open, onOpenChange, item, categoryOptions, create, update }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  item?: Investigation | null;
  categoryOptions: string[];
  create: ReturnType<typeof useCreateInvestigation>;
  update: ReturnType<typeof useUpdateInvestigation>;
}) {
  const NEW_CATEGORY = "__new__";
  const [form, setForm] = useState({ name: item?.name ?? "", category: item?.category?.trim() ?? "" });
  const [addingNew, setAddingNew] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  const submit = async () => {
    const category = addingNew ? newCategory.trim() : form.category;
    if (addingNew && !category) return;
    try {
      const body = { name: form.name, category: category || undefined };
      if (item) await update.mutateAsync({ id: item.id, body });
      else await create.mutateAsync(body);
      onOpenChange(false);
    } catch { /* handled */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{item ? "Edit investigation" : "Add investigation"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div>
            <Label>Category</Label>
            {addingNew ? (
              <div className="flex gap-2">
                <Input
                  autoFocus
                  placeholder="New category name"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
                />
                <Button variant="ghost" onClick={() => setAddingNew(false)}>Cancel</Button>
              </div>
            ) : (
              <Select value={form.category || NEW_CATEGORY} onValueChange={(v) => { if (v === NEW_CATEGORY) { setAddingNew(true); setNewCategory(""); } else setForm({ ...form, category: v }); }}>
                <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                <SelectContent>
                  {form.category && !categoryOptions.includes(form.category) && (
                    <SelectItem value={form.category}>{form.category}</SelectItem>
                  )}
                  {categoryOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  <SelectItem value={NEW_CATEGORY}>+ New category…</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!form.name || (addingNew && !newCategory.trim()) || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TemplatesTab() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("investigations.create");
  const canEdit = isAdmin || permissions.has("investigations.edit");
  const canDelete = isAdmin || permissions.has("investigations.delete");
  const { data: invData } = useInvestigations();
  const { data, isLoading } = useInvestigationTemplates();
  const { data: groupsData, isLoading: groupsLoading } = useInvestigationTemplateGroups();
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<InvestigationTemplate | null>(null);
  // Session counter bumps on every open so the dialog remounts fresh each time
  // and useState re-initializes from the current template prop (see SCHEMA.md §18 2026-07-20).
  const [sessionId, setSessionId] = useState(0);
  const openAdd = () => { setEditItem(null); setSessionId((n) => n + 1); setAddOpen(true); };
  const openEdit = (t: InvestigationTemplate) => { setEditItem(t); setSessionId((n) => n + 1); setAddOpen(true); };
  const create = useCreateInvestigationTemplate();
  const update = useUpdateInvestigationTemplate();
  const del = useDeleteInvestigationTemplate();
  const move = useMoveInvestigationTemplate();
  // Stable refs for useMemo deps (avoids the exhaustive-deps warning from
  // `?? []` creating a fresh array each render).
  const rows = useMemo(() => data?.rows ?? [], [data]);
  const groups = useMemo(() => groupsData?.rows ?? [], [groupsData]);
  const investigations = useMemo(() => invData?.rows ?? [], [invData]);
  const invById = useMemo(() => new Map(investigations.map((i) => [i.id, i])), [investigations]);

  // Group buckets (shared helper — Ungrouped pinned last). Empty groups are
  // hidden here (inspectable in the Groups tab instead).
  const buckets = useMemo(() => bucketTemplates(rows, groups), [rows, groups]);
  // Accordion expansion (null = untouched → only the Ungrouped section open;
  // named groups start COLLAPSED — the prescription-templates-tab default,
  // user request 2026-11-02, commit a7e33b6). Controlled so a fold the user
  // made by hand survives data refetches.
  const [expanded, setExpanded] = useState<string[] | null>(null);

  // Move a template into a group (or Ungrouped) via the row's ⋯ menu.
  const moveTo = (tpl: InvestigationTemplate, groupId: string | null) => {
    if (groupId === tpl.groupId) return; // already there — no-op
    move.mutate({ id: tpl.id, groupId });
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-4">
          <h2 className="font-serif text-lg text-maroon-700 dark:text-amber-500">Templates ({rows.length})</h2>
          {canCreate && <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}><Plus size={14} className="mr-1" /> Add</Button>}
        </div>
        {isLoading || groupsLoading ? (
          <div className="p-4"><Skeleton className="h-24" /></div>
        ) : rows.length === 0 ? (
          <p className="px-4 pb-4 text-center text-sm text-muted">No templates — design one from the existing tests</p>
        ) : (
          <div className="px-4 pb-4">
            <Accordion
              type="multiple"
              value={expanded ?? buckets.filter((b) => b.groupId === null).map((b) => b.key)}
              onValueChange={setExpanded}
              className="space-y-1.5"
            >
              {buckets.map((b) => (
                <AccordionItem key={b.key} value={b.key} className="border border-cream-200 rounded-lg dark:border-dark-700">
                  <AccordionTrigger className="px-3 py-2.5 text-sm font-medium text-maroon-700 hover:no-underline dark:text-amber-500">
                    <span className="flex items-center gap-2">
                      <FolderOpen size={15} className="text-maroon-600 dark:text-amber-500" />
                      {b.name}
                      <span className="text-xs font-normal text-muted">
                        {b.items.length} template{b.items.length === 1 ? "" : "s"}
                      </span>
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="space-y-1.5 px-3 pb-3">
                    {b.items.map((t) => (
                      <div
                        key={t.id}
                        className="flex items-start justify-between gap-2 rounded-lg border border-cream-200 bg-white p-2.5 dark:border-dark-700 dark:bg-dark-800"
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink dark:text-cream-50">{t.name}</span>
                          <span className="mt-0.5 block truncate text-xs text-muted">
                            {t.items.length} test{t.items.length === 1 ? "" : "s"}: {t.items.map((id) => invById.get(id)?.name ?? "?").join(", ")}
                          </span>
                        </span>
                        <span className="flex shrink-0 gap-1">
                          {canEdit && (
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(t)} title="Edit template"><Pencil size={14} /></Button>
                          )}
                          {canEdit && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="icon" variant="ghost" className="h-7 w-7" title="Move to group"><FolderInput size={14} /></Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuLabel>Move to group</DropdownMenuLabel>
                                {groups.map((g) => (
                                  <DropdownMenuItem key={g.id} disabled={g.id === t.groupId} onClick={() => moveTo(t, g.id)}>
                                    {g.name}
                                    {g.id === t.groupId ? <span className="ml-auto text-xs text-muted">current</span> : null}
                                  </DropdownMenuItem>
                                ))}
                                <DropdownMenuSeparator />
                                <DropdownMenuItem disabled={t.groupId === null} onClick={() => moveTo(t, null)}>
                                  {UNGROUPED_LABEL}
                                  {t.groupId === null ? <span className="ml-auto text-xs text-muted">current</span> : null}
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          {canDelete && (
                            <Button
                              size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                              title="Delete"
                              onClick={() => {
                                if (window.confirm(`Delete template "${t.name}"?`)) del.mutate(t.id);
                              }}
                            >
                              <Trash size={14} />
                            </Button>
                          )}
                        </span>
                      </div>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        )}
      </CardContent>
      <TemplateFormDialog
        key={sessionId}
        open={addOpen}
        onOpenChange={setAddOpen}
        template={editItem}
        investigations={investigations}
        groups={groups}
        create={create}
        update={update}
      />
    </Card>
  );
}

// ---------------- Groups tab (migration 0037, 2026-11-04) ----------------

function GroupsTab() {
  const { isAdmin, permissions } = useAuth();
  const canCreate = isAdmin || permissions.has("investigations.create");
  const canEdit = isAdmin || permissions.has("investigations.edit");
  const canDelete = isAdmin || permissions.has("investigations.delete");
  const { data, isLoading } = useInvestigationTemplateGroups();
  const { data: tplData } = useInvestigationTemplates();
  const [addOpen, setAddOpen] = useState(false);
  const [editItem, setEditItem] = useState<InvestigationTemplateGroup | null>(null);
  const [name, setName] = useState("");
  const create = useCreateInvestigationTemplateGroup();
  const update = useUpdateInvestigationTemplateGroup();
  const del = useDeleteInvestigationTemplateGroup();
  const groups = useMemo(() => data?.rows ?? [], [data]);
  const rows = useMemo(() => tplData?.rows ?? [], [tplData]);

  const openAdd = () => { setEditItem(null); setName(""); setAddOpen(true); };
  const openEdit = (g: InvestigationTemplateGroup) => { setEditItem(g); setName(g.name); setAddOpen(true); };
  const submit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      if (editItem) await update.mutateAsync({ id: editItem.id, name: trimmed, updatedAt: editItem.updatedAt });
      else await create.mutateAsync({ name: trimmed });
      setAddOpen(false);
    } catch { /* onError toast already fired */ }
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-serif text-lg text-maroon-700 dark:text-amber-500">Groups ({groups.length})</h2>
          {canCreate && (
            <Button className="bg-maroon-600 hover:bg-maroon-700 text-cream-50" onClick={openAdd}>
              <FolderPlus size={14} className="mr-1" /> Add group
            </Button>
          )}
        </div>
        {isLoading ? <Skeleton className="h-24" /> :
         groups.length === 0 ? <p className="text-sm text-muted py-8 text-center">No groups yet — create one to organize your templates.</p> :
         <div className="space-y-2">
          {groups.map((g) => {
            const count = rows.filter((t) => t.groupId === g.id).length;
            return (
              <div key={g.id} className="flex items-center justify-between rounded-lg border border-cream-200 bg-white p-3 dark:border-dark-700 dark:bg-dark-800">
                <span className="min-w-0">
                  <span className="block truncate font-serif text-base text-maroon-700 dark:text-amber-500">{g.name}</span>
                  <span className="text-xs text-muted">{count} template{count === 1 ? "" : "s"}</span>
                </span>
                <span className="flex shrink-0 gap-1">
                  {canEdit && <Button size="icon" variant="ghost" className="h-7 w-7" title="Rename group" onClick={() => openEdit(g)}><Pencil size={14} /></Button>}
                  {canDelete && (
                    <Button
                      size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                      title="Delete group"
                      onClick={() => {
                        if (window.confirm(`Delete group "${g.name}"? Its templates move to "Ungrouped" — they are NOT deleted.`)) {
                          del.mutate(g.id);
                        }
                      }}
                    >
                      <Trash size={14} />
                    </Button>
                  )}
                </span>
              </div>
            );
          })}
         </div>}
      </CardContent>
      {/* Group add/rename dialog (session-keyed so name state re-initializes). */}
      <Dialog
        key={editItem?.id ?? "new"}
        open={addOpen}
        onOpenChange={(v) => { if (!v) { setAddOpen(false); setEditItem(null); } }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{editItem ? "Rename group" : "Add group"}</DialogTitle></DialogHeader>
          <div>
            <Label>Name *</Label>
            <Input
              autoFocus
              value={name}
              placeholder="e.g. Diabetic workup"
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setAddOpen(false); setEditItem(null); }}>Cancel</Button>
            <Button
              onClick={submit}
              disabled={!name.trim() || create.isPending || update.isPending}
              className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"
            >
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function TemplateFormDialog({ open, onOpenChange, template, investigations, groups, create, update }: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  template?: InvestigationTemplate | null;
  investigations: Investigation[];
  groups: InvestigationTemplateGroup[];
  create: ReturnType<typeof useCreateInvestigationTemplate>;
  update: ReturnType<typeof useUpdateInvestigationTemplate>;
}) {
  const [form, setForm] = useState({ name: template?.name ?? "", items: template?.items ?? [] as string[] });
  const [search, setSearch] = useState("");
  const templateSearchRef = useRef<HTMLInputElement>(null);
  const invById = useMemo(() => new Map(investigations.map((i) => [i.id, i])), [investigations]);
  // "__none__" = no group selected (create mode); existing templates carry
  // a definite groupId-or-null.
  const NONE = "__none__";
  const [groupId, setGroupId] = useState<string>(template?.groupId ?? NONE);
  // Search matches investigations not already picked; an exact-name (case-insensitive)
  // match floats to the top so Enter adds the intended test even when the query
  // is a prefix of many names (e.g. "CBC").
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return [];
    return investigations
      .filter((i) => !form.items.includes(i.id) && i.name.toLowerCase().includes(q))
      .sort((a, b) => {
        const ax = a.name.toLowerCase() === q ? 0 : 1;
        const bx = b.name.toLowerCase() === q ? 0 : 1;
        return ax - bx || a.name.localeCompare(b.name);
      })
      .slice(0, 10);
  }, [search, investigations, form.items]);

  const addMatch = (inv: Investigation) => {
    setForm((f) => ({ ...f, items: [...f.items, inv.id] }));
    setSearch("");
    templateSearchRef.current?.focus();
  };

  const { highlight, highlightRef, onKeyDown } = useDropdownNav({
    count: matches.length,
    onSelect: (i) => { if (matches[i]) addMatch(matches[i]); },
    inputRef: templateSearchRef,
  });
  const removeItem = (id: string) => {
    setForm((f) => ({ ...f, items: f.items.filter((x) => x !== id) }));
  };
  const submit = async () => {
    try {
      if (template) await update.mutateAsync({ id: template.id, body: { ...form, ...(groupId !== template.groupId ? { groupId: groupId === NONE ? null : groupId } : {}) } });
      else await create.mutateAsync({ ...form, ...(groupId !== NONE ? { groupId } : {}) });
      onOpenChange(false);
    } catch { /* handled */ }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{template ? "Edit template" : "Add template"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="template-name">Name *</Label>
            <Input id="template-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div>
            <Label>Group</Label>
            <Select value={groupId} onValueChange={setGroupId}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="No group (Ungrouped)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>No group (Ungrouped)</SelectItem>
                {groups.map((g) => (
                  <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="template-search">Investigations</Label>
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
              <Input
                id="template-search"
                ref={templateSearchRef}
                className="pl-8"
                placeholder="Search and press Enter to add…"
                autoComplete="off"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={onKeyDown}
              />
              {search.trim() !== "" && matches.length > 0 && (
                <div className="visible-scrollbar absolute left-0 right-0 top-full z-10 mt-1 max-h-52 overscroll-contain overflow-auto rounded-md border border-cream-200 bg-cream-100 shadow-md">
                  {matches.map((m, i) => (
                    <button
                      key={m.id}
                      type="button"
                      ref={i === highlight ? highlightRef : undefined}
                      className={cn(
                        "flex w-full items-center justify-between gap-2 border-b border-cream-200 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-cream-200",
                        i === highlight && "bg-cream-200"
                      )}
                      onClick={() => addMatch(m)}
                    >
                      <span className="min-w-0 flex-1 truncate">{m.name}</span>
                      <span className="shrink-0 text-xs text-muted">{m.category ?? "—"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            {search.trim() !== "" && matches.length === 0 && (
              <p className="mt-1 text-xs text-muted">
                {investigations.length === 0
                  ? "No investigations in the catalog yet — add or import tests first."
                  : "No investigations match your search."}
              </p>
            )}
          </div>
          <div>
            <Label>Selected investigations ({form.items.length})</Label>
            <div className="min-h-16 rounded-md border border-cream-200 bg-cream-50/40 p-2">
              {form.items.length === 0 ? (
                <p className="px-1 py-2 text-sm text-muted">Search above and press Enter (or click a result) to add investigations to this template.</p>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {form.items.map((id) => {
                    const inv = invById.get(id);
                    return (
                      <Badge key={id} variant="secondary" className="gap-1 py-1 pl-2.5 pr-1.5 font-medium normal-case">
                        <span className="max-w-56 truncate">{inv?.name ?? "Unknown test"}</span>
                        <button
                          type="button"
                          className="rounded-full p-0.5 text-muted transition-colors hover:bg-red-100 hover:text-red-600"
                          onClick={() => removeItem(id)}
                          aria-label={`Remove ${inv?.name ?? "unknown test"}`}
                        >
                          <X size={12} strokeWidth={2.5} />
                        </button>
                      </Badge>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!form.name || create.isPending || update.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkImportTab() {
  const { isAdmin, permissions } = useAuth();
  const canImport = isAdmin || permissions.has("investigations.bulkimport");
  const fileRef = useRef<HTMLInputElement>(null);
  const bulk = useBulkImportInvestigations();
  const [preview, setPreview] = useState<{ name: string; category?: string }[] | null>(null);

  const handleFile = async (file: File) => {
    if (!/\.xlsx$/i.test(file.name)) {
      toast.error("Only .xlsx files are supported. Download the template for the expected format.");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    try {
      // Dynamic import keeps the ~430KB xlsx parser out of the main bundle;
      // it is only loaded when a user actually picks a file.
      const XLSX = await import("xlsx");
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      if (!ws) throw new Error("The workbook has no sheets.");
      const raw = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: null });
      const rows: { name: string; category?: string }[] = [];
      for (const r of raw) {
        const name = String(r.name ?? r.Name ?? "").trim();
        const category = String(r.category ?? r.Category ?? "").trim();
        if (!name) continue; // skip blank names
        rows.push({ name, category: category || undefined });
      }
      if (rows.length === 0) {
        toast.error("No rows found. The file must have a header row with `name` and `category` columns.");
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
      setPreview(rows);
    } catch (e) {
      toast.error(e instanceof Error && e.message ? `Could not read the file: ${e.message}` : "Could not read the file. Is it a valid .xlsx workbook?");
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const doImport = async () => {
    if (!preview) return;
    try {
      await bulk.mutateAsync(preview);
      setPreview(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch { /* handled */ }
  };

  const downloadTemplate = async () => {
    try {
      // window.open() can't attach the Appwrite JWT — fetch with auth and
      // hand the browser a blob URL instead.
      const blob = await apiFetchBlob("/investigations/template");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "investigations-template.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not download the template");
    }
  };

  return (
    <Card>
      <CardContent className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-lg text-maroon-700 dark:text-amber-500">Bulk Import</h2>
          <Button variant="outline" onClick={downloadTemplate}><Download size={14} className="mr-1" /> Download template</Button>
        </div>
        <p className="text-sm text-muted">
          Upload an Excel (<code>.xlsx</code>) file with columns: <code>name, category</code>. Existing items
          with the same name will be updated; new ones inserted.
        </p>
        {canImport && (
          <>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }} className="block" />
            {preview && (
              <div className="space-y-2">
                <p className="text-sm font-medium">{preview.length} rows ready to import</p>
                <div className="max-h-48 overflow-auto rounded border border-cream-200">
                  <Table>
                    <TableHeader><TableRow className="bg-cream-100"><TableHead>Name</TableHead><TableHead>Category</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {preview.slice(0, 50).map((r, i) => (
                        <TableRow key={i}><TableCell>{r.name}</TableCell><TableCell>{r.category ?? "—"}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {preview.length > 50 && <p className="p-2 text-xs text-muted">… and {preview.length - 50} more</p>}
                </div>
                <Button onClick={doImport} disabled={bulk.isPending} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"><Upload size={14} className="mr-1" /> Import {preview.length} rows</Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
